import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Trash2, Archive } from 'lucide-react';

// CTH-ADMIN-082 — report deletion / controlled archival dialog (hm_admin).
export const ReportDeleteDialog = ({ report, open, onOpenChange, onDone }) => {
  const { i18n } = useTranslation();
  const es = (i18n.language || 'es').startsWith('es');
  const L = (a, b) => (es ? a : b);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const draft = ['draft_ai_generated', 'under_review', 'edited_by_hm'].includes(report?.status);
  const terminal = ['archived', 'discarded'].includes(report?.status);

  useEffect(() => { if (open) setReason(''); }, [open]);
  if (!report) return null;

  const run = async () => {
    if (!reason.trim()) { toast.error(L('Indica el motivo', 'Provide a reason')); return; }
    setBusy(true);
    try {
      if (draft) {
        await api.post(`/admin/reports/${report.id}/discard`, { reason: reason.trim() });
        toast.success(L('Borrador eliminado (reversible desde la pestaña "discarded")', 'Draft deleted (restorable from the "discarded" tab)'));
      } else {
        await api.post(`/admin/reports/${report.id}/request-archive`, { reason_code: 'other', reason_text: reason.trim() });
        await api.post(`/admin/reports/${report.id}/archive`);
        toast.success(L('Informe retirado de las vistas activas (archivado con trazabilidad)', 'Report withdrawn from active views (archived with traceability)'));
      }
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="report-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {draft ? <Trash2 className="h-5 w-5 text-rose-600" /> : <Archive className="h-5 w-5 text-amber-600" />}
            {draft ? L('Eliminar informe (borrador)', 'Delete report (draft)') : L('Retirar informe de vistas activas', 'Withdraw report from active views')}
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm space-y-1.5">
          <p><span className="text-muted-foreground">{L('Colaborador', 'Employee')}:</span> <strong>{report.candidate?.name || '—'}</strong></p>
          <p><span className="text-muted-foreground">{L('Cliente', 'Client')}:</span> {report.company?.name || '—'}</p>
          <p className="flex items-center gap-2">
            <span className="text-muted-foreground">{L('Estado', 'Status')}:</span>
            <Badge variant="outline" className="text-xs">{report.status}</Badge>
            {report.scores?.overall_score != null && <span className="text-xs text-muted-foreground">· {L('Puntaje', 'Score')} {report.scores.overall_score}</span>}
          </p>
          {!draft && !terminal && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
              {L('Este informe tiene estado avanzado (aprobado/entregado u otro). Por requisitos de retención y auditoría se retira de las vistas activas sin destruir la evidencia.',
                 'This report has an advanced status (approved/released or other). Per retention and audit requirements it is withdrawn from active views without destroying the evidence.')}
            </p>
          )}
        </div>
        <textarea className="w-full rounded-md border border-input bg-background p-2 text-sm" rows={2}
                  placeholder={L('Motivo (obligatorio)', 'Reason (required)')}
                  value={reason} onChange={(e) => setReason(e.target.value)}
                  data-testid="report-delete-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancelar', 'Cancel')}</Button>
          <Button variant="destructive" onClick={run} disabled={busy} data-testid="report-delete-confirm" className="gap-1.5">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {draft ? L('Eliminar', 'Delete') : L('Retirar y archivar', 'Withdraw & archive')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// CTH-ADMIN-083 — evaluation deletion (soft archive) with dependency preview.
export const EvaluationDeleteDialog = ({ evaluation, open, onOpenChange, onDone }) => {
  const { i18n } = useTranslation();
  const es = (i18n.language || 'es').startsWith('es');
  const L = (a, b) => (es ? a : b);
  const [deps, setDeps] = useState(null);
  const [reason, setReason] = useState('obsolete');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !evaluation) return;
    setDeps(null); setReason('obsolete');
    api.get(`/admin/trash/assignment/${evaluation.id}/dependencies`)
      .then((r) => setDeps(r.data))
      .catch(() => setDeps({}));
  }, [open, evaluation]);
  if (!evaluation) return null;

  const blockers = deps?.blockers || [];
  const depEntries = blockers.length
    ? blockers.map((b) => [b.kind, b.count])
    : Object.entries(deps?.dependencies || {}).filter(([k, v]) => typeof v === 'number' && v > 0 && k !== 'ok');

  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/assignments/${evaluation.id}/archive`, { reason_code: reason });
      toast.success(L('Evaluación retirada de las vistas activas (restaurable desde Archivadas)', 'Evaluation removed from active views (restorable from Archived)'));
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="eval-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-rose-600" />
            {L('Eliminar evaluación', 'Delete evaluation')}
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm space-y-1.5">
          <p><strong>{evaluation.candidate?.name || '—'}</strong> · {evaluation.title}</p>
          <p className="flex items-center gap-2">
            <span className="text-muted-foreground">{L('Estado', 'Status')}:</span>
            <Badge variant="outline" className="text-xs">{evaluation.status}</Badge>
            <span className="text-xs text-muted-foreground">{evaluation.progress || 0}%</span>
          </p>
          {deps === null ? (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> {L('Verificando dependencias…', 'Checking dependencies…')}</p>
          ) : depEntries.length > 0 ? (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 space-y-0.5" data-testid="eval-delete-deps">
              <p className="font-medium">{L('Registros vinculados que se conservarán:', 'Linked records that will be preserved:')}</p>
              {depEntries.map(([k, v]) => <p key={k}>• {k}: {v}</p>)}
              <p>{L('La evaluación se retira de las vistas activas sin borrar respuestas, informes ni historial (sin eliminación en cascada).',
                    'The evaluation is removed from active views without deleting responses, reports or history (no cascading deletion).')}</p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="eval-delete-nodeps">
              {L('Sin respuestas ni registros dependientes. Eliminación suave reversible.', 'No responses or dependent records. Reversible soft deletion.')}
            </p>
          )}
        </div>
        <select className="w-full rounded-md border border-input bg-background p-2 text-sm"
                value={reason} onChange={(e) => setReason(e.target.value)} data-testid="eval-delete-reason">
          <option value="obsolete">{L('Obsoleta', 'Obsolete')}</option>
          <option value="duplicate">{L('Duplicada', 'Duplicate')}</option>
          <option value="test_residue">{L('Residuo de prueba', 'Test residue')}</option>
          <option value="founder_request">{L('Solicitud del Founder', 'Founder request')}</option>
          <option value="manual_archive">{L('Otro (archivo manual)', 'Other (manual archive)')}</option>
        </select>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancelar', 'Cancel')}</Button>
          <Button variant="destructive" onClick={run} disabled={busy || deps === null} data-testid="eval-delete-confirm" className="gap-1.5">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {L('Eliminar (reversible)', 'Delete (reversible)')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
